import React, { useMemo } from "react";
import { useHistory } from "react-router-dom";
import { NumberParam, StringParam, useQueryParams } from "use-query-params";
import { GroupIcon, TalentsIcon } from "../components/icons";
import SortAscendingDirectionIcon from "../components/icons/SortAscendingDirectionIcon";
import SortDescendingDirectionIcon from "../components/icons/SortDescendingDirectionIcon";
import List from "../components/utils/List";
import Pagination from "../components/utils/Pagination";
import SearchInput from "../components/utils/SearchInput";
import {
  Container,
  EmptyState,
  Link,
  Link as WombatLink,
  ScreenContainer,
  ScreenHeader,
  Select,
  Badge,
  ButtonGroup,
  Button,
} from "../components/wombat-ui";
import Avatar from "../components/wombat-ui/Avatar";
import { H6 } from "../components/wombat-ui/components/Typography";
import GroupBadge from "../components/groups/GroupBadge";
import { UserSummaryFragment } from "../graphql/fragments";
import useGetUsersMinimal from "../graphql/hooks/queries/useGetUsersMinimal";
import { Group, SortableVariables } from "../graphql/types";
import useTranslations from "../hooks/useTranslations";
import useViewer from "../hooks/useViewer";

export type User = {
  avatar: string;
  name: string;
  function: string;
  madsAsTalent: any[];
  id: string;
  slug: string;
};

type UserOverviewListProps = {
  user: UserSummaryFragment;
  size?: "auto" | "small" | "large";
  loading?: boolean;
};

const UserOverviewList: React.FC<UserOverviewListProps> = ({ user }) => {
  const t = useTranslations();

  return (
    <li className="py-4" id={`userItem.${user.id}`}>
      <div className="flex items-center space-x-4">
        <Avatar
          size={8}
          src={user.avatar}
          alt={`${user.name} profile picture`}
        />
        <div className="flex-1 min-w-0">
          <Link
            to={`/users/${user.slug}`}
            id={`${user.name}-link`}
            className="text-sm font-medium truncate"
          >
            {user.name}
          </Link>
          <Badge>{t(`users.${user.role}.name` as const)}</Badge>
        </div>
        <div className="flex flex-wrap justify-end max-w-lg min-w-0 gap-2">
          {user.groups.map((group: Group) => (
            <GroupBadge group={group} />
          ))}
        </div>
      </div>
    </li>
  );
};

const UserList: React.FC = () => {
  const t = useTranslations();

  const {
    company: { groups },
  } = useViewer();

  const [{ page, q, sortBy, direction, groupId }, setQuery] = useQueryParams({
    page: NumberParam,
    sortBy: StringParam,
    direction: StringParam,
    q: StringParam,
    groupId: StringParam,
  });

  const history = useHistory();

  const { data, loading } = useGetUsersMinimal({
    variables: {
      page: page || 1,
      per: 20,
      q: q || undefined,
      groupId: groupId || undefined,
      sortBy: (sortBy as SortableVariables<User>) || undefined,
      direction: direction as "ASC" | "DESC",
    },
  });

  const users = data?.users?.collection;
  const itemsCount = data?.users?.metadata?.totalCount;

  const orderOptions = useMemo(
    () => [
      { value: "", label: t("users.sortBy.blank") },
      { value: "lastName", label: t("users.sortBy.name") },
      { value: "role", label: t("users.sortBy.role") },
    ],
    []
  );

  return (
    <>
      <List
        loading={loading}
        data={users}
        listOnly
        renderAsList={(data) => <UserOverviewList user={data} key={data.id} />}
        renderEmptyState={() => {
          return <H6>{t("users.noMatchingUsers")}</H6>;
        }}
        id="users-list"
      >
        <div className="relative">
          <SearchInput
            defaultValue={q}
            onChange={(q) => setQuery({ page: 1, q })}
            label={t("list.search") as string}
            placeholder={t("users.searchPlaceholder") as string}
          />
          {groupId && (
            <div className="absolute mt-1">
              <Badge
                removable
                onRemove={() => setQuery({ page: 1, groupId: undefined })}
              >
                {groups.find((a) => a.id === groupId)?.name || groupId}
              </Badge>
            </div>
          )}
        </div>
        <div className="flex space-x-3">
          <button
            id="groupListButton"
            onClick={() => history.push("/groups/edit")}
            className="relative inline-flex items-center px-4 py-2 -ml-px space-x-2 text-sm font-medium border border-gray-300 rounded-md text-dark-100 bg-gray-50 hover:bg-gray-100 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
          >
            <GroupIcon iconClassName="w-5 h-5" />
            <span>{t("groups.list")}</span>
          </button>

          <Select
            label="role"
            srOnlyLabel
            items={orderOptions}
            defaultValue={sortBy}
            onChange={(item) =>
              setQuery({ page: 1, q, sortBy: item.value, direction })
            }
            getValue={(item) => item.value}
            renderItem={(item) => item.label}
            id={"user_role_filter"}
          />
          <ButtonGroup scale="sm">
            <Button
              icon={SortAscendingDirectionIcon}
              onClick={() => setQuery({ direction: "ASC" })}
              variant={
                direction === "ASC" || !direction ? "primary" : undefined
              }
            />
            <Button
              icon={SortDescendingDirectionIcon}
              onClick={() => setQuery({ direction: "DESC" })}
              variant={direction === "DESC" ? "primary" : undefined}
            />
          </ButtonGroup>
        </div>
      </List>
      {!loading && <Pagination itemsCount={itemsCount} itemsPerPage={20} />}
    </>
  );
};

const UserListScreen: React.FC = () => {
  const t = useTranslations();

  const { isCapableOfInsertingUsers, isCapableOfReadingUsers } = useViewer();

  return (
    <ScreenContainer
      error={
        !isCapableOfReadingUsers ? (
          <EmptyState
            title={t("network.unauthorized") as string}
            action={
              <Link variant="button" to="/">
                {t("network.backToHome")}
              </Link>
            }
          />
        ) : undefined
      }
    >
      <ScreenHeader
        title={t("users.header") as string}
        description={t("users.usersDescription") as string}
        icon={TalentsIcon}
        actions={[
          isCapableOfInsertingUsers && (
            <WombatLink variant="button" to="/users/new" key="new">
              {t("users.addUsers")}
            </WombatLink>
          ),
        ]}
      />
      <Container className="flex flex-col space-y-10">
        <UserList />
      </Container>
    </ScreenContainer>
  );
};

export default UserListScreen;
